"use client";

import { useCallback, useMemo, useState } from "react";
import { PillGroup } from "@/components/tool-kit/primitives";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  concertIsReady,
  LIVE_PERFORMANCE_FIELDS as F,
  formatLivePerformance,
  type LivePerformanceLabels,
  type PerformanceKind,
  stagedPerformanceIsReady,
} from "@/lib/tool-kit/media-sources/live-performance-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const LIVE_PERFORMANCE_CITATION_GENERATOR_STRINGS = {
  formHeading: "Performance details",
  resultsHeading: "Your citations",
  emptyPromptStaged:
    "Enter the title of the production and the date you saw it to see your citations.",
  emptyPromptConcert:
    "Enter the performer and the date of the concert to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  kindLabel: "What did you see?",
  kindStaged: "Play, musical, opera or dance",
  kindConcert: "Concert or recital",
  titleLabel: "Title of the production",
  titlePlaceholder: "Heartbreak house",
  titleHint:
    "Type it in sentence case: capitalize only the first word and proper nouns. Each style gets the capitalization it uses.",
  creatorsLabel: "Playwright or composer",
  creatorsPlaceholder: "George Bernard Shaw",
  creatorsHint: "One per line, as First Last or Last, First.",
  creditAsLabel: "Credit them as",
  creditBy: "by (a play, opera or dance)",
  creditMusicBy: "music by",
  creditMusicAndLyricsBy: "music and lyrics by",
  directorLabel: "Director",
  directorPlaceholder: "Robin Lefevre",
  conductorLabel: "Conductor",
  conductorPlaceholder: "Franz Welser-Möst",
  choreographerLabel: "Choreographer",
  choreographerPlaceholder: "Justin Peck",
  performersLabel: "Lead performers (optional)",
  performersPlaceholder: "Philip Bosco",
  performersHint: "One per line. Name only the performers you discuss.",
  companyLabel: "Company or producer",
  companyPlaceholder: "Roundabout Theatre Company",
  performerLabel: "Performer or ensemble",
  performerPlaceholder: "Beyoncé",
  performerIsGroupLabel: "This is a band, orchestra or other group",
  performerIsGroupHint:
    "A group's name is never inverted: Cleveland Orchestra, not Orchestra, Cleveland.",
  concertTitleLabel: "Tour, concert or program title (optional)",
  concertTitlePlaceholder: "The “Formation” World Tour",
  concertTitleHint: "As billed. Without one, MLA describes it as a concert.",
  venueLabel: "Venue",
  venuePlaceholderStaged: "American Airlines Theatre",
  venuePlaceholderConcert: "Rose Bowl",
  cityLabel: "City",
  cityPlaceholderStaged: "New York",
  cityPlaceholderConcert: "Pasadena",
  regionLabel: "State or province (APA)",
  regionPlaceholderStaged: "NY",
  regionPlaceholderConcert: "CA",
  dateLabel: "Date of the performance you saw",
  datePlaceholder: "YYYY-MM-DD",
  dateHint:
    "Each performance is a different event, so cite the date you attended.",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes)",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  worksCited: "Works Cited",
  inText: "In-text citation",
  footnote: "Footnote",
  shortNote: "Shortened note",
  bibliography: "Bibliography",
  mention: "Mention in your text (no reference list entry)",
  apaNote:
    "APA 7 has no reference format for a live performance: readers cannot retrieve it, so, like a live speech (§8.9), it is described in your sentence and left out of the reference list. If you watched a recording, cite the recording instead.",
  mlaNote:
    "MLA 9 cites the performance you attended: the production first, its contributors and company, then the date and the venue. In text, cite it by its title or performer.",
  chicagoNote:
    "Chicago 18 (§14.166) cites a live performance in the text or in a note. A bibliography entry is generally unnecessary.",
  exampleStaged: {
    title: "Heartbreak house",
    creators: "George Bernard Shaw",
    creditAs: "by",
    director: "Robin Lefevre",
    performers: "Philip Bosco\nSwoosie Kurtz",
    company: "Roundabout Theatre Company",
    venue: "American Airlines Theatre",
    city: "New York",
    region: "NY",
    date: "2006-10-01",
  },
  exampleConcert: {
    performer: "Beyoncé",
    title: "The “Formation” World Tour",
    venue: "Rose Bowl",
    city: "Pasadena",
    region: "CA",
    date: "2016-05-14",
  },
} as const;

type Strings = typeof LIVE_PERFORMANCE_CITATION_GENERATOR_STRINGS;

function placeFields(strings: Strings, kind: PerformanceKind): SourceField[] {
  const staged = kind === "staged";
  return [
    {
      id: F.venue,
      kind: "text",
      label: strings.venueLabel,
      placeholder: staged
        ? strings.venuePlaceholderStaged
        : strings.venuePlaceholderConcert,
    },
    {
      id: F.city,
      kind: "text",
      label: strings.cityLabel,
      placeholder: staged
        ? strings.cityPlaceholderStaged
        : strings.cityPlaceholderConcert,
    },
    {
      id: F.region,
      kind: "text",
      label: strings.regionLabel,
      placeholder: staged
        ? strings.regionPlaceholderStaged
        : strings.regionPlaceholderConcert,
    },
    {
      id: F.date,
      kind: "date",
      label: strings.dateLabel,
      placeholder: strings.datePlaceholder,
      hint: strings.dateHint,
    },
  ];
}

function stagedFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.title,
      kind: "text",
      label: strings.titleLabel,
      placeholder: strings.titlePlaceholder,
      hint: strings.titleHint,
      wide: true,
    },
    {
      id: F.creators,
      kind: "people",
      label: strings.creatorsLabel,
      placeholder: strings.creatorsPlaceholder,
      hint: strings.creatorsHint,
    },
    {
      id: F.performers,
      kind: "people",
      label: strings.performersLabel,
      placeholder: strings.performersPlaceholder,
      hint: strings.performersHint,
    },
    {
      id: F.creditAs,
      kind: "select",
      label: strings.creditAsLabel,
      options: [
        { value: "by", label: strings.creditBy },
        { value: "musicBy", label: strings.creditMusicBy },
        {
          value: "musicAndLyricsBy",
          label: strings.creditMusicAndLyricsBy,
        },
      ],
    },
    {
      id: F.director,
      kind: "text",
      label: strings.directorLabel,
      placeholder: strings.directorPlaceholder,
    },
    {
      id: F.conductor,
      kind: "text",
      label: strings.conductorLabel,
      placeholder: strings.conductorPlaceholder,
    },
    {
      id: F.choreographer,
      kind: "text",
      label: strings.choreographerLabel,
      placeholder: strings.choreographerPlaceholder,
    },
    {
      id: F.company,
      kind: "text",
      label: strings.companyLabel,
      placeholder: strings.companyPlaceholder,
      wide: true,
    },
    ...placeFields(strings, "staged"),
  ];
}

function concertFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.performer,
      kind: "text",
      label: strings.performerLabel,
      placeholder: strings.performerPlaceholder,
    },
    {
      id: F.title,
      kind: "text",
      label: strings.concertTitleLabel,
      placeholder: strings.concertTitlePlaceholder,
      hint: strings.concertTitleHint,
    },
    {
      id: F.performerIsGroup,
      kind: "checkbox",
      label: strings.performerIsGroupLabel,
      hint: strings.performerIsGroupHint,
      wide: true,
    },
    {
      id: F.conductor,
      kind: "text",
      label: strings.conductorLabel,
      placeholder: strings.conductorPlaceholder,
    },
    ...placeFields(strings, "concert"),
  ];
}

/** The widget's strings, as the formatter's labels. */
export function livePerformanceLabels(strings: Strings): LivePerformanceLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    worksCited: strings.worksCited,
    inText: strings.inText,
    footnote: strings.footnote,
    shortNote: strings.shortNote,
    bibliography: strings.bibliography,
    mention: strings.mention,
    apaNote: strings.apaNote,
    mlaNote: strings.mlaNote,
    chicagoNote: strings.chicagoNote,
  };
}

export default function LivePerformanceCitationGeneratorWidget() {
  const strings = useToolUi(LIVE_PERFORMANCE_CITATION_GENERATOR_STRINGS);
  const [kind, setKind] = useState<PerformanceKind>("staged");
  const fields = useMemo(
    () => (kind === "staged" ? stagedFields(strings) : concertFields(strings)),
    [kind, strings]
  );
  const labels = useMemo(() => livePerformanceLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatLivePerformance(kind, values, labels),
    [kind, labels]
  );
  const builderStrings = useMemo(
    () => ({
      ...strings,
      emptyPrompt:
        kind === "staged"
          ? strings.emptyPromptStaged
          : strings.emptyPromptConcert,
    }),
    [kind, strings]
  );

  return (
    <SourceCitationBuilder
      example={
        kind === "staged" ? strings.exampleStaged : strings.exampleConcert
      }
      fields={fields}
      format={format}
      intro={
        <PillGroup
          label={strings.kindLabel}
          name="live-performance-kind"
          onChange={setKind}
          options={[
            { value: "staged", label: strings.kindStaged },
            { value: "concert", label: strings.kindConcert },
          ]}
          value={kind}
        />
      }
      isReady={kind === "staged" ? stagedPerformanceIsReady : concertIsReady}
      strings={builderStrings}
    />
  );
}
